// src/services/axios.ts

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { authService } from './auth';

let navigateToLogin: (() => void) | null = null;
let logoutCallback: (() => void) | null = null;

export const setNavigateToLogin = (fn: () => void) => {
  navigateToLogin = fn;
};

export const setLogoutCallback = (fn: () => void) => {
  logoutCallback = fn;
};

const isLocalhost =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

const API_URL = isLocalhost
  ? 'http://localhost:8111/api/v1'
  : 'https://api.gotube.cc/api/v1';

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (error?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.request.use(
  (config) => {
    if (!config.headers.Authorization) {
      const token = authService.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (!originalRequest) {
      return Promise.reject(error);
    }

    // Не пытаемся обновлять токен для самих auth-эндпоинтов: 401 от /auth/refresh
    // означает, что refresh-токен недействителен, и повторный refresh создаст цикл.
    const requestUrl = originalRequest.url || '';
    const isAuthEndpoint =
      requestUrl.includes('/auth/refresh') ||
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/register');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token as string}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;
      originalRequest._retry = true;

      try {
        const tokens = await authService.refreshTokens();
        const newToken = tokens.access_token;
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        // Выкидываем на логин только если refresh-токен явно отвергнут (401/403).
        // Сетевые ошибки и 5xx не должны разлогинивать пользователя.
        const status = (refreshError as AxiosError)?.response?.status;
        if (status === 401 || status === 403) {
          logoutCallback?.();
          if (navigateToLogin && !window.location.pathname.includes('login')) {
            navigateToLogin();
          }
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
